/**
 * **Has this element ever been on screen?** — the gate under both of the feedback card's lazy
 * reads.
 *
 * Promoted here from `features/attention-queue` on 24 September 2026, when `features/survey-report`'s
 * task reading — the Pulpit's survey rows, each a `GET /customer/{id}/surveys` — became its second
 * feature consumer.
 *
 * The card draws its whole half of the queue and scrolls (`docs/design/README.md`, the
 * `2026-09-16-karty-feedbacku.html` entry), so a trainer with twenty items waiting has twenty rows
 * in the document. Two of those rows carry a read of their own — `useSessionReadout`'s
 * `GET /workout-execution/{id}/display` and `useClipMedia`'s `GET /clips/{clipId}` — and firing
 * them per ROW rather than per VISIBLE row would cost twenty requests to draw two cards.
 *
 * ── Why the observer rather than the row's INDEX ─────────────────────────────────────────────
 * `index < FEEDBACK_ROWS_IN_VIEW` (the card's own reserve count) is one line and needs no browser API, and it was the first
 * shape this took. It is wrong for the case the whole change is about: the third row's read would
 * never start, so a trainer who scrolls to it waits at a skeleton that has nothing behind it. The
 * geometry is the actual question — "is this row being looked at" — and the browser answers it
 * exactly.
 *
 * ── It LATCHES, and the observer disconnects on the first hit ────────────────────────────────
 * Scrolling a row back out must not disable its query: a disabled query with data is fine, but the
 * flapping costs a render per row per scroll and buys nothing, because the answer is already
 * cached. One hit, one disconnect, one state change for the life of the row.
 *
 * ── The ref is a PARAMETER ───────────────────────────────────────────────────────────────────
 * React Compiler's `react-hooks/refs` treats a ref reached through a hook's return value as a ref
 * read during render, so `ref={hook.ref}` fails the lint. The component owns the ref and hands it
 * in — the same shape `shared/ui/dnd/useDraggableItem.ts` settled on.
 *
 * No `rootMargin`: the intersection is computed against the viewport AND clipped by every scrolling
 * ancestor, which is what makes a row parked below the card's own fold count as off screen. A row
 * peeking by a pixel counts as on screen, which is the honest reading of "the trainer can see it".
 */

import { useEffect, useState, type RefObject } from 'react';

export function useOnScreen(ref: RefObject<Element | null>): boolean {
  const [onScreen, setOnScreen] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (element === null) return;

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      setOnScreen(true);
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return onScreen;
}
